// @ts-strict

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BACKUP_LANGUAGES, IUserBasicInfo } from '../..';
import { AppLanguages } from '../../helpers/constants';
import { GetUserBasicDataService } from '../get-user-basic-data/get-user-basic-data.service';
import { DEFAULT_LANGUAGES } from '@app/core/helpers/available-languages';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  public panelLanguage = new BehaviorSubject<string | null>(null);

  private readonly DEFAULT_LANGUAGE = 'en';
  private readonly STORAGE_KEYS = {
    currentLanguage: 'current_panel_language',
    lastNotificationsPanelOpen: 'last_notifications_panel_open'
  } as const;

  constructor(private getUserBasicDataService: GetUserBasicDataService) {
    // this.panelLanguage.next(this.getUserBasicData());  why ?!
    this.panelLanguage.next(this.getPanelLang());
  }

  public getPanelLang(): string {
    let currentPanelLanguage = localStorage.getItem('current_panel_language');
    if (!currentPanelLanguage) {
      const userInfo = this.getParsedUserBasicData();

      if (userInfo && userInfo.user?.current_language) {
        this.setPanelLang(userInfo.user.current_language);

        currentPanelLanguage = userInfo.user.current_language;
      } else {
        this.getUserBasicDataService.shareUserDataSubject.subscribe(res => {
          if (res?.user?.current_language) {
            this.setPanelLang(res.user.current_language);

            currentPanelLanguage = res.user.current_language;
          }
        });
      }
    }

    return currentPanelLanguage || AppLanguages[0].short;
  }

  public getNewsUrl(): string {
    const lang = this.getPanelLang();
    switch (lang) {
      case 'pl':
        return 'community/forum/1/Nowości';
      case 'ru':
        return 'community/forum/21/Новости';
      case 'uk':
        return 'community/forum/31/Новини';
      case 'en':
      case 'es':
      default:
        return 'community/forum/11/News';
    }
  }

  public getCheckedNewsID(): number {
    return (localStorage.getItem('latest_news_id') as unknown as number) || 0;
  }

  public updateCheckedNewsID(id: number): void {
    if (id > this.getCheckedNewsID()) {
      localStorage.setItem('latest_news_id', id.toString());
    }
  }

  public areNewsChecked(id: number): boolean {
    return id <= this.getCheckedNewsID();
  }

  public closeBanner(id: number): void {
    const closedBanners = this.getClosedBanners();
    if (!closedBanners.includes(id)) {
      closedBanners.push(id);
      localStorage.setItem('closed_banners', JSON.stringify(closedBanners));
    }
  }

  public getClosedBanners(): number[] {
    const storedBanners = localStorage.getItem('closed_banners');
    return storedBanners ? JSON.parse(storedBanners) : [];
  }

  public setPanelLang(lang: string): void {
    try {
      const availableLangs = Object.keys(DEFAULT_LANGUAGES);
      const isLanguageAvailable = availableLangs.includes(lang);

      this.setLanguage(isLanguageAvailable ? lang : this.DEFAULT_LANGUAGE);
    } catch (error) {
      console.error('Error setting panel language:', error);
      this.setLanguage(this.DEFAULT_LANGUAGE);
    }
  }

  private setLanguage(lang: string): void {
    if (this.panelLanguage) {
      this.panelLanguage.next(lang);
    }
    localStorage.setItem(this.STORAGE_KEYS.currentLanguage, lang);
  }

  public setBasicUserData(data: string): void {
    localStorage.setItem('basic_user_data', data);
  }

  public getUserBasicData = () => {
    return localStorage.getItem('basic_user_data');
  };

  public getParsedUserBasicData = (): IUserBasicInfo | null => {
    const data = localStorage.getItem('basic_user_data');

    if (data) {
      return JSON.parse(data) as IUserBasicInfo;
    }

    return null;
  };

  public setDataForRegistration(step: number, data: string): void {
    localStorage.setItem(`register_data_step_${step}`, data);
  }

  public getDataForRegistration = (step: number) => {
    return localStorage.getItem(`register_data_step_${step}`);
  };

  public setRegisterLoginFormLang(lang: string): void {
    localStorage.setItem('register_login_form_lang', lang);
  }

  public getRegisterLoginFormLang = () => {
    return localStorage.getItem('register_login_form_lang');
  };

  public removeDataForRegistration(key: string): void {
    localStorage.removeItem(key);
  }

  public isRemoteLoginActive(): boolean {
    return localStorage.getItem('remote_login_active') === 'active';
  }

  public getRecentChatSearches(): {
    username: Array<number>;
    messages: Array<string>;
  } {
    const data = localStorage.getItem('recent_chat_searches');
    const parsedData = data ? JSON.parse(data) : null;

    const recentChatSearches = {
      username: parsedData?.username || [],
      messages: parsedData?.messages || []
    };

    return recentChatSearches;
  }

  public setRecentChatSearches(items: { username: Array<number>; messages: Array<string> }): void {
    items.username.splice(10);
    items.messages.splice(10);
    localStorage.setItem('recent_chat_searches', JSON.stringify(items));
  }

  public getAnnouncementsMarkedAsReadItemsIds = (): number[] => {
    const ids = localStorage.getItem('announcements_marked_as_read_items_ids');

    if (!ids) return [];

    return JSON.parse(ids);
  };

  public checkAnnouncementReadId(id: number): boolean {
    return this.getAnnouncementsMarkedAsReadItemsIds().includes(id);
  }

  public setAnnouncementsMarkedAsReadItemsIds = (items: number[]): void => {
    localStorage.setItem('announcements_marked_as_read_items_ids', JSON.stringify(items));
  };
}
